import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff,
  Image as ImageIcon, BookOpen, Film, Calendar, Clock, X,
  GripVertical, ExternalLink, AlertTriangle, Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { API_URL } from '../config/api';

const API = API_URL;

// Slide status helper
const getSlideStatus = (slide) => {
  const now = new Date();
  const startsAt = slide.starts_at ? new Date(slide.starts_at) : null;
  const endsAt = slide.ends_at ? new Date(slide.ends_at) : null;
  
  if (!slide.active) return { status: 'inactive', color: 'bg-gray-500' };
  if (startsAt && now < startsAt) return { status: 'scheduled', color: 'bg-blue-500' };
  if (endsAt && now > endsAt) return { status: 'expired', color: 'bg-red-500' };
  return { status: 'active', color: 'bg-green-500' };
};

// Format date for display
const formatDate = (isoString) => {
  if (!isoString) return 'Not set';
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return 'Invalid date';
  }
};

// Search dialog component
const AddSlideDialog = ({ isOpen, onClose, onAdd, existingSlideIds }) => {
  const [searchTab, setSearchTab] = useState('series');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Banner slide fields
  const [bannerTitle, setBannerTitle] = useState('');
  const [bannerSubtitle, setBannerSubtitle] = useState('');
  const [bannerImageUrl, setBannerImageUrl] = useState('');
  const [bannerLinkUrl, setBannerLinkUrl] = useState('');
  
  const searchContent = useCallback(async (query, type) => {
    setLoading(true);
    try {
      const endpoint = type === 'series' 
        ? `${API}/admin/search/series`
        : `${API}/admin/search/novels`;
      
      const response = await axios.get(endpoint, {
        params: { q: query, limit: 20 },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (isOpen && searchTab !== 'banner') {
      searchContent(searchQuery, searchTab);
    }
  }, [isOpen, searchTab, searchQuery, searchContent]);
  
  const handleAddContent = (item, type) => {
    // Check if already exists
    const key = `${type}:${item.id}`;
    if (existingSlideIds.has(key)) {
      toast.error(`This ${type} is already in the carousel`);
      return;
    }
    
    onAdd({
      type,
      target_id: item.id,
      title_override: null,
      subtitle_override: null,
      image_url_override: null,
      active: true
    });
    onClose();
  };
  
  const handleAddBanner = () => {
    if (!bannerImageUrl) {
      toast.error('Banner image URL is required');
      return;
    }
    
    onAdd({
      type: 'banner',
      target_id: null,
      title_override: bannerTitle,
      subtitle_override: bannerSubtitle,
      image_url_override: bannerImageUrl,
      link_url_override: bannerLinkUrl,
      active: true
    });
    
    // Reset form
    setBannerTitle('');
    setBannerSubtitle('');
    setBannerImageUrl('');
    setBannerLinkUrl('');
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Hero Slide</DialogTitle>
        </DialogHeader>
        
        <Tabs value={searchTab} onValueChange={setSearchTab} className="flex-1 flex flex-col">
          <TabsList className="bg-zinc-800 border border-zinc-700">
            <TabsTrigger value="series" className="data-[state=active]:bg-orange-600">
              <Film className="w-4 h-4 mr-2" />
              Series
            </TabsTrigger>
            <TabsTrigger value="novel" className="data-[state=active]:bg-purple-600">
              <BookOpen className="w-4 h-4 mr-2" />
              Novels
            </TabsTrigger>
            <TabsTrigger value="banner" className="data-[state=active]:bg-blue-600">
              <ImageIcon className="w-4 h-4 mr-2" />
              Banner
            </TabsTrigger>
          </TabsList>
          
          {/* Series/Novel Search */}
          <TabsContent value="series" className="flex-1 flex flex-col mt-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search series by title..."
                className="pl-10 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-2 max-h-[400px]">
              {loading ? (
                <div className="text-center py-8 text-zinc-400">Searching...</div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-8 text-zinc-400">No results found</div>
              ) : (
                searchResults.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 cursor-pointer"
                    onClick={() => handleAddContent(item, 'series')}
                  >
                    <div className="flex items-center gap-3">
                      {item.hero_banner_url || item.cover_url ? (
                        <img src={item.hero_banner_url || item.cover_url} alt={item.title} className="w-16 h-10 object-cover rounded" />
                      ) : (
                        <div className="w-16 h-10 bg-zinc-700 rounded flex items-center justify-center">
                          <ImageIcon className="w-5 h-5 text-zinc-500" />
                        </div>
                      )}
                      <div>
                        <h4 className="font-medium text-white">{item.title}</h4>
                        <p className="text-sm text-zinc-400">
                          by {item.creator?.display_name || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.has_hero_banner ? (
                        <Badge variant="secondary" className="bg-green-500/20 text-green-400 text-xs">
                          Banner Ready
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400 text-xs">
                          No Banner
                        </Badge>
                      )}
                      <Badge variant="secondary" className="bg-orange-500/20 text-orange-400">
                        Series
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="novel" className="flex-1 flex flex-col mt-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search novels by title..."
                className="pl-10 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-2 max-h-[400px]">
              {loading ? (
                <div className="text-center py-8 text-zinc-400">Searching...</div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-8 text-zinc-400">No results found</div>
              ) : (
                searchResults.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 cursor-pointer"
                    onClick={() => handleAddContent(item, 'novel')}
                  >
                    <div className="flex items-center gap-3">
                      {item.hero_banner_url || item.cover_url ? (
                        <img src={item.hero_banner_url || item.cover_url} alt={item.title} className="w-16 h-10 object-cover rounded" />
                      ) : (
                        <div className="w-16 h-10 bg-zinc-700 rounded flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-zinc-500" />
                        </div>
                      )}
                      <div>
                        <h4 className="font-medium text-white">{item.title}</h4>
                        <p className="text-sm text-zinc-400">
                          by {item.creator?.display_name || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.has_hero_banner ? (
                        <Badge variant="secondary" className="bg-green-500/20 text-green-400 text-xs">
                          Banner Ready
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400 text-xs">
                          No Banner
                        </Badge>
                      )}
                      <Badge variant="secondary" className="bg-purple-500/20 text-purple-400">
                        Novel
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
          
          {/* Banner Form */}
          <TabsContent value="banner" className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Banner Title</label>
              <Input
                value={bannerTitle}
                onChange={(e) => setBannerTitle(e.target.value)}
                placeholder="Featured Announcement"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Subtitle</label>
              <Input
                value={bannerSubtitle}
                onChange={(e) => setBannerSubtitle(e.target.value)}
                placeholder="Check out our latest updates..."
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Image URL <span className="text-red-400">*</span>
              </label>
              <Input
                value={bannerImageUrl}
                onChange={(e) => setBannerImageUrl(e.target.value)}
                placeholder="https://example.com/banner.jpg"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
              <p className="text-xs text-zinc-500 mt-1">Recommended: 1920x600 (16:5 aspect ratio)</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Link URL</label>
              <Input
                value={bannerLinkUrl}
                onChange={(e) => setBannerLinkUrl(e.target.value)}
                placeholder="/announcements/new-feature"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            {bannerImageUrl && (
              <div className="mt-4">
                <p className="text-sm text-zinc-400 mb-2">Preview:</p>
                <div className="relative aspect-[16/5] bg-zinc-800 rounded-lg overflow-hidden">
                  <img 
                    src={bannerImageUrl} 
                    alt="Banner preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              </div>
            )}
            
            <Button
              onClick={handleAddBanner}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Banner Slide
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Slide edit dialog
const EditSlideDialog = ({ isOpen, onClose, slide, onSave }) => {
  const [titleOverride, setTitleOverride] = useState('');
  const [subtitleOverride, setSubtitleOverride] = useState('');
  const [imageUrlOverride, setImageUrlOverride] = useState('');
  const [linkUrlOverride, setLinkUrlOverride] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [active, setActive] = useState(true);
  
  useEffect(() => {
    if (slide) {
      setTitleOverride(slide.title_override || '');
      setSubtitleOverride(slide.subtitle_override || '');
      setImageUrlOverride(slide.image_url_override || '');
      setLinkUrlOverride(slide.link_url_override || '');
      setStartsAt(slide.starts_at ? slide.starts_at.slice(0, 16) : '');
      setEndsAt(slide.ends_at ? slide.ends_at.slice(0, 16) : '');
      setActive(slide.active !== false);
    }
  }, [slide]);
  
  const handleSave = () => {
    // Validate dates
    if (startsAt && endsAt) {
      const startDate = new Date(startsAt);
      const endDate = new Date(endsAt);
      if (endDate < startDate) {
        toast.error('End date cannot be before start date');
        return;
      }
    }
    
    onSave({
      ...slide,
      title_override: titleOverride || null,
      subtitle_override: subtitleOverride || null,
      image_url_override: imageUrlOverride || null,
      link_url_override: linkUrlOverride || null,
      starts_at: startsAt ? new Date(startsAt).toISOString() : null,
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      active
    });
    onClose();
  };
  
  if (!slide) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Slide</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Status Toggle */}
          <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
            <div>
              <h4 className="font-medium text-white">Active</h4>
              <p className="text-sm text-zinc-400">Show this slide in the carousel</p>
            </div>
            <Switch checked={active} onCheckedChange={setActive} />
          </div>
          
          {/* Override Fields */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Title Override
            </label>
            <Input
              value={titleOverride}
              onChange={(e) => setTitleOverride(e.target.value)}
              placeholder="Leave empty to use default"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Subtitle Override
            </label>
            <Input
              value={subtitleOverride}
              onChange={(e) => setSubtitleOverride(e.target.value)}
              placeholder="Leave empty to use default"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Image URL Override
            </label>
            <Input
              value={imageUrlOverride}
              onChange={(e) => setImageUrlOverride(e.target.value)}
              placeholder="Leave empty to use default"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Link URL Override
            </label>
            <Input
              value={linkUrlOverride}
              onChange={(e) => setLinkUrlOverride(e.target.value)}
              placeholder="Leave empty to use default"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          {/* Schedule Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date/Time
              </label>
              <Input
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                End Date/Time
              </label>
              <Input
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>
        </div>
        
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} className="border-zinc-700">
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-orange-600 hover:bg-orange-700">
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Remove confirmation dialog
const RemoveSlideDialog = ({ isOpen, onClose, slide, onConfirm }) => {
  if (!slide) return null;
  
  const title = slide.target?.title || slide.title_override || 'Banner Slide';
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            Remove Slide
          </DialogTitle>
        </DialogHeader>
        
        <p className="text-zinc-300">
          Are you sure you want to remove <strong>"{title}"</strong> from the hero carousel?
        </p>
        
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} className="border-zinc-700">
            Cancel
          </Button>
          <Button 
            onClick={() => { onConfirm(slide.id); onClose(); }}
            className="bg-red-600 hover:bg-red-700"
          >
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main component
const HeroCarouselManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slides, setSlides] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [removingSlide, setRemovingSlide] = useState(null);
  
  // Load slides
  const loadSlides = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/site/hero`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSlides(response.data.slides || []);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to load hero slides:', error);
      toast.error('Failed to load hero carousel');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadSlides();
  }, []);
  
  // Save all slides
  const saveSlides = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site/hero`, 
        { slides: slides.map((s, idx) => ({ ...s, order: idx })) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Hero Carousel updated');
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save hero slides:', error);
      toast.error(error.response?.data?.detail || 'Failed to save hero carousel');
    } finally {
      setSaving(false);
    }
  };
  
  // Add slide
  const handleAddSlide = async (slideData) => {
    try {
      const response = await axios.post(`${API}/admin/site/hero/add`, slideData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        loadSlides();
      }
    } catch (error) {
      console.error('Failed to add slide:', error);
      toast.error(error.response?.data?.detail || 'Failed to add slide');
    }
  };
  
  // Update slide locally
  const handleUpdateSlide = (updatedSlide) => {
    setSlides(prev => prev.map(s => s.id === updatedSlide.id ? updatedSlide : s));
    setHasChanges(true);
  };
  
  // Remove slide
  const handleRemoveSlide = async (slideId) => {
    try {
      const response = await axios.delete(`${API}/admin/site/hero/${slideId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        toast.success('Slide removed');
        loadSlides();
      }
    } catch (error) {
      console.error('Failed to remove slide:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove slide');
    }
  };
  
  // Toggle slide active
  const toggleSlideActive = (slideId) => {
    setSlides(prev => prev.map(s => 
      s.id === slideId ? { ...s, active: !s.active } : s
    ));
    setHasChanges(true);
  };
  
  // Move slide up
  const moveSlideUp = (index) => {
    if (index === 0) return;
    const newSlides = [...slides];
    [newSlides[index - 1], newSlides[index]] = [newSlides[index], newSlides[index - 1]];
    newSlides.forEach((s, i) => s.order = i);
    setSlides(newSlides);
    setHasChanges(true);
  };
  
  // Move slide down
  const moveSlideDown = (index) => {
    if (index === slides.length - 1) return;
    const newSlides = [...slides];
    [newSlides[index], newSlides[index + 1]] = [newSlides[index + 1], newSlides[index]];
    newSlides.forEach((s, i) => s.order = i);
    setSlides(newSlides);
    setHasChanges(true);
  };
  
  // Get existing slide IDs for duplicate check
  const existingSlideIds = new Set(
    slides
      .filter(s => s.type !== 'banner')
      .map(s => `${s.type}:${s.target_id}`)
  );
  
  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="py-12">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-zinc-700 border-t-orange-500 rounded-full animate-spin mb-4" />
            <p className="text-zinc-400">Loading hero carousel...</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid="hero-carousel-manager">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Film className="w-5 h-5 text-orange-400" />
              Hero Carousel Manager
            </CardTitle>
            <CardDescription>
              Curate the slides that appear in the homepage hero carousel
            </CardDescription>
          </div>
          
          {hasChanges && (
            <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400">
              Unsaved changes
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="add-series-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Series
          </Button>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="add-novel-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Novel
          </Button>
          <Button
            onClick={() => setShowAddDialog(true)}
            variant="outline"
            className="border-zinc-700 text-zinc-300"
            data-testid="add-banner-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Banner Slide
          </Button>
        </div>
        
        {/* Slides List */}
        {slides.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <Film className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No slides in the hero carousel</p>
            <p className="text-sm">Click "Add Series" or "Add Novel" to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {slides.map((slide, index) => {
              const { status, color } = getSlideStatus(slide);
              const title = slide.target?.title || slide.title_override || 'Banner';
              const hasHeroBanner = slide.target?.has_hero_banner || slide.type === 'banner';
              const imageUrl = slide.image_url_override || slide.target?.hero_banner_url || slide.target?.billboard_url || slide.target?.cover_url;
              
              return (
                <div
                  key={slide.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border ${
                    slide.active ? 'bg-zinc-800 border-zinc-700' : 'bg-zinc-900 border-zinc-800 opacity-60'
                  }`}
                  data-testid={`hero-slide-${index}`}
                >
                  {/* Drag Handle */}
                  <GripVertical className="w-5 h-5 text-zinc-500 cursor-move" />
                  
                  {/* Order Number */}
                  <span className="text-2xl font-bold text-orange-500 w-8">
                    {index + 1}
                  </span>
                  
                  {/* Thumbnail - 21:9 aspect ratio */}
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={title}
                      className="w-24 h-10 object-cover rounded"
                    />
                  ) : (
                    <div className="w-24 h-10 bg-zinc-700 rounded flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 text-zinc-500" />
                    </div>
                  )}
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-white truncate">{title}</h4>
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                      <Badge
                        variant="secondary"
                        className={
                          slide.type === 'series' 
                            ? 'bg-orange-500/20 text-orange-400'
                            : slide.type === 'novel'
                            ? 'bg-purple-500/20 text-purple-400'
                            : 'bg-blue-500/20 text-blue-400'
                        }
                      >
                        {slide.type}
                      </Badge>
                      {/* Hero Banner Status Badge */}
                      {slide.type !== 'banner' && (
                        hasHeroBanner ? (
                          <Badge variant="secondary" className="bg-green-500/20 text-green-400 text-xs">
                            Banner Ready
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400 text-xs flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            No Banner
                          </Badge>
                        )
                      )}
                      {slide.target?.creator && (
                        <span>by {slide.target.creator.display_name}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Status Badge */}
                  <Badge variant="secondary" className={`${color} text-white capitalize`}>
                    {status}
                  </Badge>
                  
                  {/* Schedule Info */}
                  {(slide.starts_at || slide.ends_at) && (
                    <div className="text-xs text-zinc-400 hidden md:block">
                      {slide.starts_at && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(slide.starts_at)}
                        </div>
                      )}
                      {slide.ends_at && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(slide.ends_at)}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    {/* Active Toggle */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleSlideActive(slide.id)}
                      className={slide.active ? 'text-green-400' : 'text-zinc-500'}
                      title={slide.active ? 'Active' : 'Inactive'}
                    >
                      {slide.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    
                    {/* Edit */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingSlide(slide)}
                      className="text-zinc-400 hover:text-white"
                      title="Edit"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    
                    {/* Move Up */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveSlideUp(index)}
                      disabled={index === 0}
                      className="text-zinc-400 hover:text-white disabled:opacity-30"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    
                    {/* Move Down */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveSlideDown(index)}
                      disabled={index === slides.length - 1}
                      className="text-zinc-400 hover:text-white disabled:opacity-30"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    
                    {/* Remove */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRemovingSlide(slide)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Save Button */}
        {hasChanges && (
          <div className="pt-4 border-t border-zinc-800">
            <Button
              onClick={saveSlides}
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="save-hero-btn"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Hero Carousel
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
      
      {/* Dialogs */}
      <AddSlideDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAdd={handleAddSlide}
        existingSlideIds={existingSlideIds}
      />
      
      <EditSlideDialog
        isOpen={!!editingSlide}
        onClose={() => setEditingSlide(null)}
        slide={editingSlide}
        onSave={handleUpdateSlide}
      />
      
      <RemoveSlideDialog
        isOpen={!!removingSlide}
        onClose={() => setRemovingSlide(null)}
        slide={removingSlide}
        onConfirm={handleRemoveSlide}
      />
    </Card>
  );
};

export default HeroCarouselManager;
